import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

import Navbar from '../../components/navbar/navbar'
import SideFilter, { FilterValues } from '../../components/side-filter'
import ListProject from '../../components/list-project'
import Footer from '../../components/footer'

import { API_BASE, getImageUrl } from '../../utils/api'
import { useCity } from '../../context/CityContext'
import ComparePanel from './sections/ComparePanel';
import ProjectHeaderMap from './sections/ProjectHeaderMap';
import { toast } from 'react-toastify'

interface Project {
    _id: string;
    name: string;
    location: string;
    address?: string;
    priceRange: string;
    minPrice?: number;
    maxPrice?: number;
    status: string;
    projectType: string;
    configuration: string;
    images: string[];
    featuredImage?: string;
    coverImage?: string;
    gallery?: { exterior: string[] };
    overview?: {
        priceRange?: string;
        configuration?: string;
        projectArea?: string;
        floors?: string;
        totalTowers?: string;
        totalUnits?: string;
    };
    developerDetails?: { name: string; logo?: string };
    developer?: { name: string; logo?: string };
    mapDetails?: {
        latitude?: string;
        longitude?: string;
    };
    createdAt?: string;
}

export default function ProjectList() {
    const [searchParams] = useSearchParams()
    const [show, setShow] = useState<boolean>(false)
    const [projects, setProjects] = useState<Project[]>([])
    const [allProjectsForMap, setAllProjectsForMap] = useState<Project[]>([])
    const [loading, setLoading] = useState(true)
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [limit] = useState(10)
    const [totalPages, setTotalPages] = useState(1)

    const { selectedCity, setSelectedCity, cities, allLocalities } = useCity()
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

    const getSubCategories = (category: string) => {
        if (category === 'Commercial') {
            return ['Office Space', 'Shop', 'Showroom', 'Warehouse', 'Land'];
        }
        if (category === 'Plots') {
            return ['Residential Plot', 'Commercial Plot'];
        }
        if (category === 'Land') {
            return ['Agricultural Land', 'Industrial Land'];
        }
        return ['Flat', 'Villa', 'Penthouse', 'Duplex', 'Bungalow', 'Studio'];
    };

    // Sidebar Lead Form state
    const [leadName, setLeadName] = useState('')
    const [leadPhone, setLeadPhone] = useState('')
    const [leadEmail, setLeadEmail] = useState('')
    const [leadSubmitting, setLeadSubmitting] = useState(false)
    const [siteLogo, setSiteLogo] = useState<string>('/logo.png')



    const [filters, setFilters] = useState<FilterValues>({
        search: searchParams.get('search') || '',
        city: searchParams.get('city') || (selectedCity && selectedCity !== 'All Cities' && selectedCity !== 'Detecting Location...' ? selectedCity : ''),
        type: searchParams.get('type') || '',
        bedrooms: searchParams.get('bedrooms') || '',
        minPrice: searchParams.get('minPrice') || '',
        maxPrice: searchParams.get('maxPrice') || '',
        subCategory: searchParams.get('subCategory') || '', furnishing: '', minSize: '', maxSize: '',
        bathrooms: '', buildingAge: '', sellType: '', facing: '',
        postedBy: '', parkingType: '', constructionStatus: '',
        reraRegistered: searchParams.get('reraRegistered') || '', balcony: '',
        liftAvailable: false, powerBackup: false, gatedCommunity: false,
        cornerProperty: false, parkFacing: false, nearbyMetro: false,
        listingType: searchParams.get('listingType') || '',
        projectStatus: searchParams.get('projectStatus') || '',
        locality: searchParams.get('locality') || ''
    })

    // Update filters when URL parameters change (dynamic navigation)
    useEffect(() => {
        const cityParam = searchParams.get('city');
        const listingTypeParam = searchParams.get('listingType');
        const subCategoryParam = searchParams.get('subCategory');
        const localityParam = searchParams.get('locality');

        setFilters(prev => ({
            ...prev,
            city: cityParam || prev.city,
            listingType: listingTypeParam || prev.listingType,
            subCategory: subCategoryParam || prev.subCategory,
            type: searchParams.get('type') || prev.type,
            projectStatus: searchParams.get('projectStatus') || prev.projectStatus,
            reraRegistered: searchParams.get('reraRegistered') || prev.reraRegistered,
            locality: localityParam || prev.locality
        }));

        if (cityParam && selectedCity !== cityParam && setSelectedCity) {
            setSelectedCity(cityParam);
        }
    }, [searchParams, selectedCity, setSelectedCity]);

    // Handle initial city from context if no URL param
    useEffect(() => {
        const cityParam = searchParams.get('city');
        if (!cityParam && selectedCity && selectedCity !== 'Detecting Location...' && selectedCity !== 'All Cities') {
            setFilters(prev => ({ ...prev, city: selectedCity }));
        }
    }, [selectedCity, searchParams]);

    useEffect(() => {
        const fetchLogo = async () => {
            try {
                const res = await fetch(`${API_BASE}/system-settings`);
                const data = await res.json();
                if (data && data.siteLogo) {
                    setSiteLogo(getImageUrl(data.siteLogo));
                }
            } catch (err) {
                console.error('Failed to fetch logo:', err);
            }
        };
        fetchLogo();
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0)
        setLoading(true)
        const params = new URLSearchParams()
        params.set('page', String(page))
        params.set('limit', String(limit))
        if (filters.search) params.set('search', filters.search)
        if (filters.city) params.set('city', filters.city)
        if (filters.locality) params.set('locality', filters.locality)
        if (filters.minPrice) params.set('minPrice', filters.minPrice)
        if (filters.maxPrice) params.set('maxPrice', filters.maxPrice)
        if (filters.listingType) params.set('listingType', filters.listingType)
        if (filters.subCategory) params.set('subCategory', filters.subCategory)
        if (filters.type) params.set('propertyType', filters.type)
        if (filters.projectStatus) params.set('projectStatus', filters.projectStatus)
        if (filters.reraRegistered) params.set('reraRegistered', filters.reraRegistered)

        fetch(`${API_BASE}/projects?${params.toString()}`)
            .then(r => r.json())
            .then(data => {
                if (Array.isArray(data)) {
                    const filteredData = data;
                    setTotal(filteredData.length);
                    setTotalPages(Math.ceil(filteredData.length / limit) || 1);
                    const startIndex = (page - 1) * limit;
                    const paginatedData = filteredData.slice(startIndex, startIndex + limit);
                    setProjects(paginatedData);
                    setAllProjectsForMap(filteredData);
                } else if (data.projects) {
                    setProjects(data.projects)
                    setAllProjectsForMap(data.allProjects || data.projects)
                    setTotal(data.total || data.projects.length)
                    setTotalPages(data.totalPages || 1)
                }
            })
            .catch(console.error)
            .finally(() => setLoading(false))
    }, [page, limit, filters])

    const handleFilterChange = (newFilters: FilterValues) => {
        setFilters(newFilters)
        setPage(1)
    }



    const handleLeadSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!leadName || !leadPhone) {
            toast.error('Please enter name and phone number')
            return
        }
        setLeadSubmitting(true)
        try {
            const res = await fetch(`${API_BASE}/leads/public-inquiry`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: leadName,
                    mobile: leadPhone,
                    email: leadEmail,
                    source: 'Online',
                    notes: 'Expert advice requested from Project List page (Property Inquiry Form).'
                })
            })
            const data = await res.json()
            if (data._id) {
                toast.success('Your request has been sent! Our expert will connect soon.')
                setLeadName('')
                setLeadPhone('')
                setLeadEmail('')
            } else {
                toast.error(data.message || 'Submission failed')
            }
        } catch (err) {
            console.error(err)
            toast.error('Server error. Please try again.')
        } finally {
            setLeadSubmitting(false)
        }
    }

    // Compare Properties state
    const [isCompareOpen, setIsCompareOpen] = useState(false);
    const [compareList, setCompareList] = useState<Project[]>([]);

    useEffect(() => {
        const stored = localStorage.getItem('compareList');
        if (stored) {
            try {
                setCompareList(JSON.parse(stored));
            } catch (e) {
                console.error("Failed to parse compareList", e);
            }
        }

        const handleCompareUpdate = () => {
            const updated = localStorage.getItem('compareList');
            if (updated) {
                try {
                    setCompareList(JSON.parse(updated));
                } catch (e) {
                    console.error("Failed to parse compareList", e);
                }
            }
        };

        window.addEventListener('compareUpdated', handleCompareUpdate);
        return () => window.removeEventListener('compareUpdated', handleCompareUpdate);
    }, [])

    const removeFromCompare = (id: string) => {
        const updated = compareList.filter((p: any) => p._id !== id);
        setCompareList(updated as Project[]);
        localStorage.setItem('compareList', JSON.stringify(updated));
        window.dispatchEvent(new Event('compareUpdated'));
    };

    const handleCompare = (project: Project) => {
        try {
            const existing = localStorage.getItem('compareList');
            let list = existing ? JSON.parse(existing) : [];
            if (!list.find((p: any) => p._id === project._id)) {
                if (list.length >= 3) {
                    alert('You can compare up to 3 properties at a time.');
                } else {
                    list.push(project);
                    localStorage.setItem('compareList', JSON.stringify(list));
                    window.dispatchEvent(new Event('compareUpdated'));
                }
            }
            setIsCompareOpen(true);
        } catch (error) {
            console.error('Error adding to compare:', error);
        }
    };



    return (
        <div className="min-h-screen bg-slate-50">
            <Navbar transparent={false} />

            <ComparePanel
                isCompareOpen={isCompareOpen}
                setIsCompareOpen={setIsCompareOpen}
                compareList={compareList as any}
                removeFromCompare={removeFromCompare}
                getImageUrl={getImageUrl}
            />

            {/* Map Section */}
            {!loading && allProjectsForMap.length > 0 && (
                <ProjectHeaderMap projects={allProjectsForMap as any} city={filters.city} />
            )}

            {/* SideFilter slide-out modal wrapper */}
            <div className={`fixed inset-0 z-50 transition-all duration-300 ${show ? 'visible' : 'invisible pointer-events-none'}`}>
                <div className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${show ? 'opacity-100' : 'opacity-0'}`} onClick={() => setShow(false)} />
                <div className={`absolute left-0 top-0 h-full w-80 bg-white shadow-2xl transition-transform duration-300 overflow-y-auto p-6 ${show ? 'translate-x-0' : '-translate-x-full'}`}>
                    <div className="flex items-center justify-between mb-6">
                        <h4 className="text-lg font-extrabold text-slate-900">Advanced Filters</h4>
                        <button onClick={() => setShow(false)} className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center">
                            <i className="fa-solid fa-xmark"></i>
                        </button>
                    </div>
                    <SideFilter show={true} setShow={setShow} filters={filters} onFilterChange={handleFilterChange} />
                </div>
            </div>

            <div className="container mx-auto px-4 pb-24 pt-8">
                {/* Header title */}
                <div className="mb-6">
                    <h1 className="text-2xl md:text-3xl font-extrabold text-black tracking-tight">
                        {filters.subCategory || filters.type || 'Residential'} Properties {filters.listingType === 'For Rent' ? (filters.type === 'Commercial' ? 'For Lease' : 'For Rent') : 'For Sale'}{filters.city ? ` in ${filters.city}` : ''}
                    </h1>
                    <p className="text-black font-extrabold text-xs mt-1">{total} matches synchronized to platform registry.</p>
                </div>

                {/* Pill Filters Bar */}
                <div className="bg-white border border-slate-100 rounded-2xl shadow-lg p-2 mb-8 flex flex-wrap items-center gap-x-1 gap-y-2 z-30 relative">
                    {/* Category Selector */}
                    <div className="relative">
                        <button 
                            onClick={() => setActiveDropdown(activeDropdown === 'category' ? null : 'category')}
                            className="px-2.5 py-1.5 hover:bg-slate-50 rounded-lg text-base font-black text-black transition-all flex items-center gap-1.5"
                        >
                            <span className="text-base">{filters.type === 'Commercial' ? '🏢' : '🏠'}</span>
                            <span>{filters.type || 'Residential'}</span>
                            <i className="fa-solid fa-chevron-down text-[10px] text-slate-400"></i>
                        </button>
                        {activeDropdown === 'category' && (
                            <div className="absolute left-0 mt-2 w-44 bg-white rounded-xl shadow-xl border border-slate-100 p-2 z-50 animate__animated animate__fadeIn animate__faster">
                                {['Residential', 'Commercial', 'Plots', 'Land'].map(cat => (
                                    <button
                                        key={cat}
                                        onClick={() => {
                                            handleFilterChange({ 
                                                ...filters, 
                                                type: cat,
                                                subCategory: '',
                                                bedrooms: '',
                                                projectStatus: ''
                                            });
                                            setActiveDropdown(null);
                                        }}
                                        className={`w-full text-left px-3 py-2 text-sm font-bold rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-between ${filters.type === cat ? 'text-blue-600 bg-blue-50' : 'text-slate-700'}`}
                                    >
                                        <span>{cat}</span>
                                        {filters.type === cat && <i className="fa-solid fa-check"></i>}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="hidden md:block h-6 w-[1px] bg-slate-200 self-center mx-0.5"></div>

                    {/* City Selector */}
                    <div className="relative">
                        <button 
                            onClick={() => setActiveDropdown(activeDropdown === 'city' ? null : 'city')}
                            className="px-2.5 py-1.5 hover:bg-slate-50 rounded-lg text-base font-black text-black transition-all flex items-center gap-1.5"
                        >
                            <span>{filters.city || 'All Cities'}</span>
                            <i className="fa-solid fa-chevron-down text-[10px] text-slate-400"></i>
                        </button>
                        {activeDropdown === 'city' && (
                            <div className="absolute left-0 mt-2 w-48 max-h-60 overflow-y-auto bg-white rounded-xl shadow-xl border border-slate-100 p-2 z-50 animate__animated animate__fadeIn animate__faster">
                                <button
                                    onClick={() => {
                                        handleFilterChange({ ...filters, city: '' });
                                        setActiveDropdown(null);
                                    }}
                                    className={`w-full text-left px-3 py-2 text-sm font-bold rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-between ${!filters.city ? 'text-blue-600 bg-blue-50' : 'text-slate-700'}`}
                                >
                                    <span>All Cities</span>
                                    {!filters.city && <i className="fa-solid fa-check"></i>}
                                </button>
                                {cities.map((cty: string) => (
                                    <button
                                        key={cty}
                                        onClick={() => {
                                            handleFilterChange({ ...filters, city: cty });
                                            setActiveDropdown(null);
                                        }}
                                        className={`w-full text-left px-3 py-2 text-sm font-bold rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-between ${filters.city === cty ? 'text-blue-600 bg-blue-50' : 'text-slate-700'}`}
                                    >
                                        <span>{cty}</span>
                                        {filters.city === cty && <i className="fa-solid fa-check"></i>}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="hidden md:block h-6 w-[1px] bg-slate-200 self-center mx-0.5"></div>

                    {/* Search Input field */}
                    <div className="relative min-w-[110px] flex-1 max-w-[160px]">
                        <i className="fa-solid fa-magnifying-glass absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
                        <input
                            type="text"
                            placeholder="Search..."
                            value={filters.search}
                            onChange={e => handleFilterChange({ ...filters, search: e.target.value })}
                            className="w-full pl-7 pr-2 py-1.5 bg-slate-50/50 hover:bg-slate-50 focus:bg-white border border-slate-100 rounded-lg text-base font-bold text-black placeholder-slate-400 outline-none transition-all"
                        />
                    </div>

                    <div className="hidden md:block h-6 w-[1px] bg-slate-200 self-center mx-0.5"></div>

                    {/* Conditional layouts rendering */}
                    {filters.type === 'Commercial' ? (
                        <>
                            {/* Commercial Layout: Buy / Lease Pill Toggle */}
                            <div className="bg-slate-100 p-0.5 rounded-full flex items-center border border-slate-200">
                                <button
                                    onClick={() => handleFilterChange({ ...filters, listingType: 'For Sale' })}
                                    className={`px-2.5 py-1 rounded-full text-base font-black transition-all active:scale-95 ${
                                        filters.listingType !== 'For Rent' 
                                            ? 'bg-white text-black shadow-sm' 
                                            : 'text-slate-500 hover:text-black'
                                    }`}
                                >
                                    Buy
                                </button>
                                <button
                                    onClick={() => handleFilterChange({ ...filters, listingType: 'For Rent' })}
                                    className={`px-2.5 py-1 rounded-full text-base font-black transition-all active:scale-95 ${
                                        filters.listingType === 'For Rent' 
                                            ? 'bg-white text-black shadow-sm' 
                                            : 'text-slate-500 hover:text-black'
                                    }`}
                                >
                                    Lease
                                </button>
                            </div>

                            <div className="hidden md:block h-6 w-[1px] bg-slate-200 self-center mx-0.5"></div>

                            {/* Commercial Type Selector (subCategory) */}
                            <div className="relative">
                                <button 
                                    onClick={() => setActiveDropdown(activeDropdown === 'subcat' ? null : 'subcat')}
                                    className="px-2.5 py-1.5 hover:bg-slate-50 rounded-lg text-base font-black text-black transition-all flex items-center gap-1.5"
                                >
                                    <span>{filters.subCategory || 'Type'}</span>
                                    <i className="fa-solid fa-chevron-down text-[10px] text-slate-400"></i>
                                </button>
                                {activeDropdown === 'subcat' && (
                                    <div className="absolute left-0 mt-2 w-48 max-h-60 overflow-y-auto bg-white rounded-xl shadow-xl border border-slate-100 p-2 z-50 animate__animated animate__fadeIn animate__faster">
                                        {getSubCategories(filters.type).map(sub => (
                                            <button
                                                key={sub}
                                                onClick={() => {
                                                    handleFilterChange({ ...filters, subCategory: filters.subCategory === sub ? '' : sub });
                                                    setActiveDropdown(null);
                                                }}
                                                className={`w-full text-left px-3 py-2 text-sm font-bold rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-between ${filters.subCategory === sub ? 'text-blue-600 bg-blue-50' : 'text-slate-700'}`}
                                            >
                                                <span>{sub}</span>
                                                {filters.subCategory === sub && <i className="fa-solid fa-check"></i>}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="hidden md:block h-6 w-[1px] bg-slate-200 self-center mx-0.5"></div>

                            {/* Commercial Budget Selector */}
                            <div className="relative">
                                <button 
                                    onClick={() => setActiveDropdown(activeDropdown === 'price' ? null : 'price')}
                                    className="px-2.5 py-1.5 hover:bg-slate-50 rounded-lg text-base font-black text-black transition-all flex items-center gap-1.5"
                                >
                                    <span>{filters.minPrice || filters.maxPrice ? 'Budget Set' : 'Budget'}</span>
                                    <i className="fa-solid fa-chevron-down text-[10px] text-slate-400"></i>
                                </button>
                                {activeDropdown === 'price' && (
                                    <div className="absolute left-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-slate-100 p-4 z-50 animate__animated animate__fadeIn animate__faster">
                                        <div className="grid grid-cols-2 gap-3 mb-4">
                                            <div>
                                                <label className="text-xs font-bold text-slate-400 block mb-1">Min Price</label>
                                                <select 
                                                    value={filters.minPrice} 
                                                    onChange={e => handleFilterChange({ ...filters, minPrice: e.target.value })}
                                                    className="w-full bg-slate-50 border border-slate-100 rounded-lg p-2 text-sm font-bold outline-none"
                                                >
                                                    <option value="">Min</option>
                                                    <option value="1000000">₹ 10 L</option>
                                                    <option value="2000000">₹ 20 L</option>
                                                    <option value="5000000">₹ 50 L</option>
                                                    <option value="10000000">₹ 1 Cr</option>
                                                    <option value="20000000">₹ 2 Cr</option>
                                                    <option value="50000000">₹ 5 Cr</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-slate-400 block mb-1">Max Price</label>
                                                <select 
                                                    value={filters.maxPrice} 
                                                    onChange={e => handleFilterChange({ ...filters, maxPrice: e.target.value })}
                                                    className="w-full bg-slate-50 border border-slate-100 rounded-lg p-2 text-sm font-bold outline-none"
                                                >
                                                    <option value="">Max</option>
                                                    <option value="2000000">₹ 20 L</option>
                                                    <option value="5000000">₹ 50 L</option>
                                                    <option value="10000000">₹ 1 Cr</option>
                                                    <option value="20000000">₹ 2 Cr</option>
                                                    <option value="50000000">₹ 5 Cr</option>
                                                    <option value="100000000">₹ 10 Cr</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                                            <button 
                                                onClick={() => handleFilterChange({ ...filters, minPrice: '', maxPrice: '' })}
                                                className="text-xs font-bold text-rose-500 hover:underline"
                                            >
                                                Reset
                                            </button>
                                            <button 
                                                onClick={() => setActiveDropdown(null)}
                                                className="px-3 py-1.5 bg-[#05144b] text-white rounded-lg text-xs font-bold"
                                            >
                                                Apply
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Residential (Default) Layout: Localities Selector */}
                            <div className="relative">
                                <button 
                                    onClick={() => setActiveDropdown(activeDropdown === 'locality' ? null : 'locality')}
                                    className="px-2.5 py-1.5 hover:bg-slate-50 rounded-lg text-base font-black text-black transition-all flex items-center gap-1.5"
                                >
                                    <span>{filters.locality || 'Localities'}</span>
                                    <i className="fa-solid fa-chevron-down text-[10px] text-slate-400"></i>
                                </button>
                                {activeDropdown === 'locality' && (
                                    <div className="absolute left-0 mt-2 w-64 max-h-60 overflow-y-auto bg-white rounded-xl shadow-xl border border-slate-100 p-2 z-50 animate__animated animate__fadeIn animate__faster">
                                        <button
                                            onClick={() => {
                                                handleFilterChange({ ...filters, locality: '' });
                                                setActiveDropdown(null);
                                            }}
                                            className={`w-full text-left px-3 py-2 text-sm font-bold rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-between ${!filters.locality ? 'text-blue-600 bg-blue-50' : 'text-slate-700'}`}
                                        >
                                            <span>All Localities</span>
                                            {!filters.locality && <i className="fa-solid fa-check"></i>}
                                        </button>
                                        {allLocalities
                                            .filter((loc: any) => !filters.city || loc.cityName?.trim().toLowerCase() === filters.city?.trim().toLowerCase())
                                            .map((loc: any) => (
                                                <button
                                                    key={loc._id}
                                                    onClick={() => {
                                                        handleFilterChange({ ...filters, locality: loc.name });
                                                        setActiveDropdown(null);
                                                    }}
                                                    className={`w-full text-left px-3 py-2 text-sm font-bold rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-between ${filters.locality === loc.name ? 'text-blue-600 bg-blue-50' : 'text-slate-700'}`}
                                                >
                                                    <span>{loc.name}</span>
                                                    {filters.locality === loc.name && <i className="fa-solid fa-check"></i>}
                                                </button>
                                            ))
                                        }
                                    </div>
                                )}
                            </div>

                            <div className="hidden md:block h-6 w-[1px] bg-slate-200 self-center mx-0.5"></div>

                            {/* BHK Selector */}
                            <div className="relative">
                                <button 
                                    onClick={() => setActiveDropdown(activeDropdown === 'bhk' ? null : 'bhk')}
                                    className="px-2.5 py-1.5 hover:bg-slate-50 rounded-lg text-base font-black text-black transition-all flex items-center gap-1.5"
                                >
                                    <span>{filters.bedrooms ? `${filters.bedrooms} BHK` : 'BHK'}</span>
                                    <i className="fa-solid fa-chevron-down text-[10px] text-slate-400"></i>
                                </button>
                                {activeDropdown === 'bhk' && (
                                    <div className="absolute left-0 mt-2 w-40 bg-white rounded-xl shadow-xl border border-slate-100 p-2 z-50 animate__animated animate__fadeIn animate__faster">
                                        {['1', '2', '3', '4'].map(b => (
                                            <button
                                                key={b}
                                                onClick={() => {
                                                    handleFilterChange({ ...filters, bedrooms: filters.bedrooms === b ? '' : b });
                                                    setActiveDropdown(null);
                                                }}
                                                className={`w-full text-left px-3 py-2 text-sm font-bold rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-between ${filters.bedrooms === b ? 'text-blue-600 bg-blue-50' : 'text-slate-700'}`}
                                            >
                                                <span>{b} BHK</span>
                                                {filters.bedrooms === b && <i className="fa-solid fa-check"></i>}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="hidden md:block h-6 w-[1px] bg-slate-200 self-center mx-0.5"></div>

                            {/* Budget Selector */}
                            <div className="relative">
                                <button 
                                    onClick={() => setActiveDropdown(activeDropdown === 'price' ? null : 'price')}
                                    className="px-2.5 py-1.5 hover:bg-slate-50 rounded-lg text-base font-black text-black transition-all flex items-center gap-1.5"
                                >
                                    <span>{filters.minPrice || filters.maxPrice ? 'Budget Set' : 'Budget'}</span>
                                    <i className="fa-solid fa-chevron-down text-[10px] text-slate-400"></i>
                                </button>
                                {activeDropdown === 'price' && (
                                    <div className="absolute left-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-slate-100 p-4 z-50 animate__animated animate__fadeIn animate__faster">
                                        <div className="grid grid-cols-2 gap-3 mb-4">
                                            <div>
                                                <label className="text-xs font-bold text-slate-400 block mb-1">Min Price</label>
                                                <select 
                                                    value={filters.minPrice} 
                                                    onChange={e => handleFilterChange({ ...filters, minPrice: e.target.value })}
                                                    className="w-full bg-slate-50 border border-slate-100 rounded-lg p-2 text-sm font-bold outline-none"
                                                >
                                                    <option value="">Min</option>
                                                    <option value="1000000">₹ 10 L</option>
                                                    <option value="2000000">₹ 20 L</option>
                                                    <option value="5000000">₹ 50 L</option>
                                                    <option value="10000000">₹ 1 Cr</option>
                                                    <option value="20000000">₹ 2 Cr</option>
                                                    <option value="50000000">₹ 5 Cr</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-slate-400 block mb-1">Max Price</label>
                                                <select 
                                                    value={filters.maxPrice} 
                                                    onChange={e => handleFilterChange({ ...filters, maxPrice: e.target.value })}
                                                    className="w-full bg-slate-50 border border-slate-100 rounded-lg p-2 text-sm font-bold outline-none"
                                                >
                                                    <option value="">Max</option>
                                                    <option value="2000000">₹ 20 L</option>
                                                    <option value="5000000">₹ 50 L</option>
                                                    <option value="10000000">₹ 1 Cr</option>
                                                    <option value="20000000">₹ 2 Cr</option>
                                                    <option value="50000000">₹ 5 Cr</option>
                                                    <option value="100000000">₹ 10 Cr</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                                            <button 
                                                onClick={() => handleFilterChange({ ...filters, minPrice: '', maxPrice: '' })}
                                                className="text-xs font-bold text-rose-500 hover:underline"
                                            >
                                                Reset
                                            </button>
                                            <button 
                                                onClick={() => setActiveDropdown(null)}
                                                className="px-3 py-1.5 bg-[#05144b] text-white rounded-lg text-xs font-bold"
                                            >
                                                Apply
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="hidden md:block h-6 w-[1px] bg-slate-200 self-center mx-0.5"></div>

                            {/* Possession Selector */}
                            <div className="relative">
                                <button 
                                    onClick={() => setActiveDropdown(activeDropdown === 'status' ? null : 'status')}
                                    className="px-2.5 py-1.5 hover:bg-slate-50 rounded-lg text-base font-black text-black transition-all flex items-center gap-1.5"
                                >
                                    <span>{filters.projectStatus || 'Possession'}</span>
                                    <i className="fa-solid fa-chevron-down text-[10px] text-slate-400"></i>
                                </button>
                                {activeDropdown === 'status' && (
                                    <div className="absolute left-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-slate-100 p-2 z-50 animate__animated animate__fadeIn animate__faster">
                                        {['Newly Launched', 'Under Construction', 'Ready to Move'].map(s => (
                                            <button
                                                key={s}
                                                onClick={() => {
                                                    handleFilterChange({ ...filters, projectStatus: filters.projectStatus === s ? '' : s });
                                                    setActiveDropdown(null);
                                                }}
                                                className={`w-full text-left px-3 py-2 text-sm font-bold rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-between ${filters.projectStatus === s ? 'text-blue-600 bg-blue-50' : 'text-slate-700'}`}
                                            >
                                                <span>{s}</span>
                                                {filters.projectStatus === s && <i className="fa-solid fa-check"></i>}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="hidden md:block h-6 w-[1px] bg-slate-200 self-center mx-0.5"></div>

                            {/* Type Selector (subCategory) */}
                            <div className="relative">
                                <button 
                                    onClick={() => setActiveDropdown(activeDropdown === 'subcat' ? null : 'subcat')}
                                    className="px-2.5 py-1.5 hover:bg-slate-50 rounded-lg text-base font-black text-black transition-all flex items-center gap-1.5"
                                >
                                    <span>{filters.subCategory || 'Type'}</span>
                                    <i className="fa-solid fa-chevron-down text-[10px] text-slate-400"></i>
                                </button>
                                {activeDropdown === 'subcat' && (
                                    <div className="absolute left-0 mt-2 w-48 max-h-60 overflow-y-auto bg-white rounded-xl shadow-xl border border-slate-100 p-2 z-50 animate__animated animate__fadeIn animate__faster">
                                        {getSubCategories(filters.type).map(sub => (
                                            <button
                                                key={sub}
                                                onClick={() => {
                                                    handleFilterChange({ ...filters, subCategory: filters.subCategory === sub ? '' : sub });
                                                    setActiveDropdown(null);
                                                }}
                                                className={`w-full text-left px-3 py-2 text-sm font-bold rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-between ${filters.subCategory === sub ? 'text-blue-600 bg-blue-50' : 'text-slate-700'}`}
                                            >
                                                <span>{sub}</span>
                                                {filters.subCategory === sub && <i className="fa-solid fa-check"></i>}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {/* Clear Button */}
                    <button 
                        onClick={() => handleFilterChange({
                            ...filters,
                            search: '', minPrice: '', maxPrice: '', bedrooms: '', projectStatus: '', reraRegistered: '', locality: '', subCategory: '', type: ''
                        })}
                        className="ml-auto px-3 py-2 text-rose-600 hover:text-rose-700 text-base font-black transition-all flex items-center gap-1"
                    >
                        <i className="fa-solid fa-xmark text-sm"></i>
                        <span>Clear</span>
                    </button>
                </div>

                {/* Two-Column Page Content Area */}
                <div className="flex flex-col lg:flex-row gap-8">
                    
                    {/* Left Column: Listings & Pagination (72% width) */}
                    <div className="w-full lg:w-[72%]">
                        {loading ? (
                            <div className="text-center py-32 bg-white rounded-2xl border border-slate-100 shadow-sm flex flex-col items-center">
                                <div className="w-12 h-12 border-4 border-slate-200 border-t-[#05144b] rounded-full animate-spin"></div>
                                <p className="text-slate-400 font-bold mt-6 text-sm">Fetching Matching Listings...</p>
                            </div>
                        ) : projects.length === 0 ? (
                            <div className="text-center py-24 bg-white rounded-2xl border border-slate-100 shadow-sm flex flex-col items-center p-6">
                                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-6 text-slate-300 border">
                                    <i className="fa-solid fa-building-circle-exclamation text-2xl"></i>
                                </div>
                                <h5 className="text-xl font-bold text-slate-900 mb-2">No Properties Found</h5>
                                <p className="text-slate-400 font-medium max-w-sm mx-auto text-sm mb-8">We couldn't find any listings matching your search options.</p>
                                <button
                                    className="px-6 py-3 bg-[#05144b] hover:bg-blue-900 text-white rounded-xl font-bold text-xs shadow-lg transition-all active:scale-95"
                                    onClick={() => handleFilterChange({ ...filters, search: '', minPrice: '', maxPrice: '', bedrooms: '', projectStatus: '', reraRegistered: '' })}
                                >
                                    Reset Filters
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {/* Results Count & Sort bar */}
                                <div className="flex items-center justify-between mb-4 bg-white px-4 py-3 rounded-xl border border-slate-100 shadow-sm">
                                    <span className="text-[12px] md:text-sm font-bold text-slate-600">
                                        Showing <span className="text-black font-extrabold">{projects.length > 0 ? (page - 1) * limit + 1 : 0}-{Math.min(page * limit, total)}</span> of <span className="text-black font-extrabold">{total}</span> Properties
                                    </span>

                                    {/* Sort Dropdown */}
                                    <div className="flex items-center gap-3">
                                        <span className="text-xs font-bold text-slate-500">Sort By:</span>
                                        <div className="relative">
                                            <select className="appearance-none bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg pl-3 pr-8 py-1.5 text-xs font-bold text-slate-700 focus:outline-none cursor-pointer">
                                                <option>Default</option>
                                                <option>Price: Low to High</option>
                                                <option>Price: High to Low</option>
                                                <option>Newest First</option>
                                            </select>
                                            <i className="fa-solid fa-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none"></i>
                                        </div>
                                    </div>
                                </div>

                                {/* Project List */}
                                <div className="grid grid-cols-1 gap-6">
                                    {projects.map((item) => (
                                        <div key={item._id}>
                                            <ListProject item={item as any} handleCompare={handleCompare} />
                                        </div>
                                    ))}
                                </div>

                                {/* Pagination Footer */}
                                {totalPages > 1 && (
                                    <div className="flex items-center justify-center gap-2 pt-10">
                                        <button
                                            disabled={page === 1}
                                            onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                                            className="w-10 h-10 rounded-full border border-slate-200 bg-white flex items-center justify-center hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95 text-slate-700"
                                        >
                                            <i className="fa-solid fa-chevron-left text-xs"></i>
                                        </button>
                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                                            <button
                                                key={p}
                                                onClick={() => setPage(p)}
                                                className={`w-10 h-10 rounded-full text-xs font-bold transition-all active:scale-95 ${page === p ? 'bg-[#05144b] text-white shadow' : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'}`}
                                            >
                                                {p}
                                            </button>
                                        ))}
                                        <button
                                            disabled={page === totalPages}
                                            onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                                            className="w-10 h-10 rounded-full border border-slate-200 bg-white flex items-center justify-center hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95 text-slate-700"
                                        >
                                            <i className="fa-solid fa-chevron-right text-xs"></i>
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Right Column: Sticky Widgets Sidebar (28% width) */}
                    <div className="w-full lg:w-[28%] space-y-6">
                        {/* Inquiry / Callback Form Widget */}
                        <div className="bg-white rounded-2xl border border-slate-100 shadow-xl p-6 sticky top-24">
                            <div className="flex flex-col items-center text-center mb-1.5 pb-2 border-b border-slate-100">
                                {/* Site Logo */}
                                <div className="h-16 max-w-[180px] flex items-center justify-center mb-1">
                                    <img 
                                        src={siteLogo} 
                                        alt="MyPropertyGuru Logo" 
                                        className="h-full object-contain" 
                                    />
                                </div>
                                <h3 className="font-extrabold text-[#05144b] text-base leading-tight">Talk to our Experts</h3>
                                <p className="text-xs font-bold text-emerald-600 mt-1">Callback within 15 minutes</p>
                            </div>
                            
                            <p className="text-sm font-bold text-slate-700 leading-relaxed mb-1.5 text-center">
                                Need help finding the perfect property? Leave your details below and a property expert will help guide your search.
                            </p>

                            <form onSubmit={handleLeadSubmit} className="space-y-2.5">
                                <div className="relative">
                                    <i className="fa-solid fa-user absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
                                    <input 
                                        type="text" 
                                        required
                                        placeholder="Name" 
                                        value={leadName}
                                        onChange={e => setLeadName(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-black outline-none focus:bg-white focus:border-[#05144b] focus:ring-1 focus:ring-[#05144b] transition-all"
                                    />
                                </div>
                                <div className="relative">
                                    <i className="fa-solid fa-phone absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
                                    <input 
                                        type="text" 
                                        required
                                        placeholder="Phone Number" 
                                        value={leadPhone}
                                        onChange={e => setLeadPhone(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-black outline-none focus:bg-white focus:border-[#05144b] focus:ring-1 focus:ring-[#05144b] transition-all"
                                    />
                                </div>
                                <div className="relative">
                                    <i className="fa-solid fa-envelope absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
                                    <input 
                                        type="email" 
                                        placeholder="Email Address (Optional)" 
                                        value={leadEmail}
                                        onChange={e => setLeadEmail(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-black outline-none focus:bg-white focus:border-[#05144b] focus:ring-1 focus:ring-[#05144b] transition-all"
                                    />
                                </div>
                                <button 
                                    type="submit"
                                    disabled={leadSubmitting}
                                    className="w-full py-3.5 text-white rounded-xl text-sm font-extrabold transition-all shadow-md active:scale-95 disabled:opacity-50 hover:opacity-90"
                                    style={{ background: 'linear-gradient(90deg, #05144b 0%, #4c1d95 100%)' }}
                                >
                                    {leadSubmitting ? 'Sending...' : 'Request callback'}
                                </button>
                            </form>
                        </div>
                    </div>

                </div>
            </div>

            <Footer />
        </div>
    )
}
